/**
 * The V3 homepage's built-in section copy — TREATX-V3-CMS-GAP-01.
 *
 * These are exactly the strings `LandingPageV3.jsx` falls back to today (EN from
 * the component, TR from `i18n/locales/tr.json`). They are used for two things
 * only: as the placeholder shown in the editor, and as the initial value when
 * the owner adopts a section ("Start managing"), so adopting a section never
 * changes what the page renders. The page keeps its own fallbacks — this file
 * is not a content authority.
 */

export const V3_SECTION_DEFAULTS = {
  hero: {
    eyebrow: { en: 'International healthcare marketplace',
      tr: 'Uluslararası sağlık hizmetleri pazar yeri' },
    title: { en: 'Find the right healthcare provider — and ask them yourself.',
      tr: 'Doğru sağlık hizmeti sağlayıcısını bulun — ve sorularınızı doğrudan kendisine sorun.' },
    description: { en: 'Discover treatments that providers genuinely offer, compare what each one publishes, message them directly and receive a written quotation prepared for your case.',
      tr: 'Sağlayıcıların gerçekten sunduğu tedavileri keşfedin, her birinin yayımladığı bilgileri karşılaştırın, doğrudan mesaj gönderin ve kendi durumunuz için hazırlanmış yazılı bir teklif alın.' },
  },
  hero_search: {
    title: { en: 'What are you looking for?', tr: 'Ne arıyorsunuz?' },
    description: { en: 'Only treatments currently offered by providers on TreatX.',
      tr: "Yalnızca TreatX'teki sağlayıcıların hâlihazırda sunduğu tedaviler." },
    link_label: { en: 'Any treatment', tr: 'Tüm tedaviler' },
    cta_label: { en: 'Explore providers', tr: 'Sağlayıcıları keşfedin' },
    footnote: { en: 'Browsing needs no account.', tr: 'Göz atmak için hesap gerekmez.' },
  },
  how_it_works: {
    eyebrow: { en: 'How TreatX works', tr: 'TreatX nasıl çalışır' },
    title: { en: 'Four steps, entirely at your pace',
      tr: 'Tamamen kendi hızınızda, dört adım' },
    description: { en: '', tr: '' },
  },
  treatments: {
    eyebrow: { en: 'Explore treatments', tr: 'Tedavileri keşfedin' },
    title: { en: 'Available through providers on TreatX',
      tr: "TreatX'teki sağlayıcılar aracılığıyla sunulanlar" },
    description: { en: 'Every treatment listed here is offered by at least one eligible provider.',
      tr: 'Burada listelenen her tedavi, en az bir uygun sağlayıcı tarafından sunulmaktadır.' },
  },
  providers: {
    eyebrow: { en: 'Explore healthcare providers', tr: 'Sağlık hizmeti sağlayıcılarını keşfedin' },
    title: { en: 'See what each provider has published',
      tr: 'Her sağlayıcının yayımladığı bilgileri görün' },
    description: { en: 'A sample of providers in the platform’s standard listing order, which takes account of the verification status and verified doctors a provider has on record. It is not a quality ranking, a recommendation or paid placement — and only credentials TreatX has verified are shown.',
      tr: "Platformun standart listeleme sırasına göre sağlayıcılardan bir seçki; bu sıralama, sağlayıcının doğrulama durumunu ve kayıtlı doğrulanmış doktorlarını dikkate alır. Bir kalite sıralaması, tavsiye veya ücretli yerleşim değildir — ve yalnızca TreatX'in doğruladığı belgeler gösterilir." },
    link_label: { en: 'View provider', tr: 'Sağlayıcıyı görüntüle' },
    cta_label: { en: 'See all providers', tr: 'Tüm sağlayıcıları gör' },
    cta_href: '/find-clinics',
  },
  showcase: {
    eyebrow: { en: 'Inside TreatX', tr: "TreatX'in içinden" },
    title: { en: 'What the experience looks like', tr: 'Deneyim nasıl görünüyor' },
    description: { en: 'Discovery, provider comparison, direct messages and written quotations — all in one place.',
      tr: 'Keşif, sağlayıcı karşılaştırma, doğrudan mesajlar ve yazılı teklifler — hepsi tek bir yerde.' },
    badge_label: { en: 'Product image pending review', tr: 'Ürün görseli incelemeyi bekliyor' },
  },
  why: {
    eyebrow: { en: 'Why TreatX', tr: 'Neden TreatX' },
    title: { en: 'Clarity before any commitment', tr: 'Herhangi bir taahhütten önce netlik' },
    description: { en: '', tr: '' },
  },
  certifications: {
    title: { en: 'Certifications', tr: 'Belgeler' },
    description: { en: 'Visibility only — the certificates themselves stay in Corporate Identity.',
      tr: 'Yalnızca görünürlük — belgelerin kendisi Kurumsal Kimlik bölümünde yönetilir.' },
  },
  provider_cta: {
    // The headline, body and the primary button label of this section are owned
    // by Provider Benefits (`provider_benefits_hero` / `_cta`) and are NOT
    // duplicated here — only the fields this record actually decides.
    eyebrow: { en: 'For healthcare providers', tr: 'Sağlık hizmeti sağlayıcıları için' },
    cta_href: '/provider/register',
    cta_label_secondary: { en: 'Explore provider benefits', tr: 'Sağlayıcı avantajlarını inceleyin' },
    cta_href_secondary: '/provider-benefits',
  },
  founding: {
    title: { en: 'Founding provider programme', tr: 'Kurucu Klinik Programı' },
    description: { en: 'Providers joining during the launch cohort use the TreatX CRM without a subscription charge. Payment-service charges and marketplace commercial terms are separate and always apply.',
      tr: 'Lansman döneminde katılan klinikler için CRM abonelik ücreti alınmaz. Ödeme hizmeti ücretleri ve pazar yeri ticari koşulları ayrıdır ve her durumda geçerlidir.' },
    footnote: { en: 'Exact terms are confirmed in your provider agreement.',
      tr: 'Kesin koşullar sağlayıcı sözleşmenizde teyit edilir.' },
  },
  academy: {
    eyebrow: { en: 'TreatX Academy', tr: 'TreatX Akademi' },
    title: { en: 'Professional training for clinic teams',
      tr: 'Klinik ekipleri için profesyonel eğitim' },
    description: { en: 'Practical, in-person training for the people who speak with international patients every day — enquiry handling, quotation practice and patient follow-up.',
      tr: 'Her gün uluslararası hastalarla konuşan ekipler için uygulamalı, yüz yüze eğitim — talep yönetimi, teklif hazırlama ve hasta takibi.' },
    badge_label: { en: 'Planned session details will be announced',
      tr: 'Planlanan oturum detayları duyurulacaktır' },
    footnote: { en: 'Registration is not open yet.', tr: 'Kayıtlar henüz açılmadı.' },
  },
  apps: {
    title: { en: 'TreatX for Android and iOS', tr: 'Android ve iOS için TreatX' },
    description: { en: 'The mobile applications are in preparation. TreatX works in your mobile browser today.',
      tr: 'Mobil uygulamalar hazırlanıyor. TreatX bugün mobil tarayıcınızda çalışır.' },
    badge_label: { en: 'Coming soon', tr: 'Yakında' },
  },
  final_cta: {
    title: { en: 'Start with a question, not a commitment.',
      tr: 'Bir taahhütle değil, bir soruyla başlayın.' },
    description: { en: 'Find a provider, ask what you need to know and decide in your own time.',
      tr: 'Bir sağlayıcı bulun, bilmek istediklerinizi sorun ve kendi zamanınızda karar verin.' },
    cta_label: { en: 'Find a provider', tr: 'Sağlayıcı bulun' },
    cta_href: '/find-clinics',
    cta_label_secondary: { en: 'Join as a provider', tr: 'Sağlayıcı olarak katılın' },
    cta_href_secondary: '/provider/register',
  },
};

export const defaultsFor = (key) => V3_SECTION_DEFAULTS[key] || {};

// The built-in child items of a section, in page order and in both languages.
// Adoption copies exactly these, so a section looks identical before and after.
export const V3_SECTION_ITEM_DEFAULTS = {
  hero: [
    { title: { en: 'No obligation', tr: 'Yükümlülük yok' } },
    { title: { en: 'You choose who to contact',
      tr: 'Kiminle iletişime geçeceğinize siz karar verirsiniz' } },
    { title: { en: 'Written, itemised offers',
      tr: 'Yazılı, kalemlere ayrılmış teklifler' } },
  ],
  how_it_works: [
    { icon: 'Search', title: { en: 'Discover treatments', tr: 'Tedavileri keşfedin' },
      description: { en: 'Browse treatments that healthcare providers on TreatX actually offer today.',
        tr: "TreatX'teki sağlık hizmeti sağlayıcılarının bugün gerçekten sunduğu tedavilere göz atın." } },
    { icon: 'Building2', title: { en: 'Explore providers', tr: 'Sağlayıcıları inceleyin' },
      description: { en: 'Review provider details and the credentials TreatX has verified, side by side.',
        tr: "Sağlayıcı bilgilerini ve TreatX'in doğruladığı belgeleri yan yana karşılaştırın." } },
    { icon: 'MessageSquare', title: { en: 'Communicate directly', tr: 'Doğrudan iletişim kurun' },
      description: { en: 'Message providers yourself. Your contact details stay under your control.',
        tr: 'Sağlayıcılara kendiniz mesaj gönderin. İletişim bilgileriniz sizin kontrolünüzde kalır.' } },
    { icon: 'FileText', title: { en: 'Request a personal offer', tr: 'Size özel teklif isteyin' },
      description: { en: 'Receive a written, itemised quotation prepared for your own case.',
        tr: 'Kendi durumunuz için hazırlanmış, yazılı ve kalemlere ayrılmış bir teklif alın.' } },
  ],
  why: [
    { icon: 'Building2', title: { en: 'Published provider details', tr: 'Yayımlanmış sağlayıcı bilgileri' },
      description: { en: 'See the information a provider publishes about itself, including verified credentials.',
        tr: 'Bir sağlayıcının kendisi hakkında yayımladığı bilgileri, doğrulanmış belgeleri dahil olmak üzere görün.' } },
    { icon: 'MessageSquare', title: { en: 'Direct conversation', tr: 'Doğrudan görüşme' },
      description: { en: 'Ask your questions yourself and keep the whole exchange in one thread.',
        tr: 'Sorularınızı kendiniz sorun ve tüm yazışmayı tek bir başlıkta tutun.' } },
    { icon: 'FileText', title: { en: 'Written quotations', tr: 'Yazılı teklifler' },
      description: { en: 'Receive an itemised offer you can read, keep and compare.',
        tr: 'Okuyabileceğiniz, saklayabileceğiniz ve karşılaştırabileceğiniz, kalemlere ayrılmış bir teklif alın.' } },
    { icon: 'ShieldCheck', title: { en: 'Your details, your choice', tr: 'Bilgileriniz, sizin tercihiniz' },
      description: { en: 'You decide which provider receives your information and when.',
        tr: 'Bilgilerinizi hangi sağlayıcının, ne zaman alacağına siz karar verirsiniz.' } },
  ],
  founding: [
    { title: { en: 'CRM subscription — waived for the launch cohort',
      tr: 'CRM aboneliği — lansman döneminde katılan klinikler için ücret alınmaz' } },
    { title: { en: 'Payment-service charges — separate, always apply',
      tr: 'Ödeme hizmeti ücretleri — ayrıdır, her durumda geçerlidir' } },
    { title: { en: 'Marketplace commercial terms — separate, always apply',
      tr: 'Pazar yeri ticari koşulları — ayrıdır, her durumda geçerlidir' } },
  ],
};

export const itemDefaultsFor = (key) => V3_SECTION_ITEM_DEFAULTS[key] || [];

// Page order, the editable fields each section actually renders, and the child
// items it supports. Nothing here invents a field the page does not read.
export const V3_SECTIONS = [
  { key: 'hero', label: 'Hero', order: 1,
    blurb: 'The opening headline, intro text, the trust bullets beside it and the product screenshot under it.',
    fields: ['eyebrow', 'title', 'description'],
    visuals: 'hero_visual',
    items: { label: 'Trust bullets', withBody: false, sectionKey: 'hero' } },
  { key: 'hero_search', label: 'Hero — treatment search panel', order: 2,
    blurb: 'The wording of the search card. The treatment list itself stays live platform data.',
    fields: ['title', 'description', 'link_label', 'cta', 'footnote'],
    linkLabel: 'Empty-selection label ("Any treatment")',
    footnoteLabel: 'Note under the button' },
  { key: 'how_it_works', label: 'How it works', order: 3,
    blurb: 'Section heading and the four step cards.',
    fields: ['eyebrow', 'title', 'description'],
    items: { label: 'Steps', withBody: true, withIcon: true } },
  { key: 'treatments', label: 'Treatments', order: 4,
    blurb: 'Heading and description. The treatment chips come from the live catalogue.',
    fields: ['eyebrow', 'title', 'description'] },
  { key: 'providers', label: 'Providers', order: 5,
    blurb: 'Heading, description, the in-card link label and the "see all" button. Provider cards stay live data.',
    fields: ['eyebrow', 'title', 'description', 'link_label', 'cta'],
    linkLabel: 'In-card link label ("View provider")' },
  { key: 'showcase', label: 'Product showcase', order: 6,
    blurb: 'Heading, description, the placeholder caption and the EN/TR screenshots of each scene.',
    fields: ['eyebrow', 'title', 'description', 'badge_label'],
    visuals: 'showcase',
    badgeLabel: 'Placeholder caption while an image is unapproved' },
  { key: 'why', label: 'Why TreatX', order: 7,
    blurb: 'Section heading and the four points.',
    fields: ['eyebrow', 'title', 'description'],
    items: { label: 'Points', withBody: true, withIcon: true } },
  { key: 'certifications', label: 'Certifications', order: 8,
    blurb: 'Visibility only. The certificates are managed in Corporate Identity and are not duplicated here.',
    fields: [] },
  { key: 'provider_cta', label: 'For providers', order: 9,
    blurb: 'The small label above the heading, the second button and this section\'s button destinations.',
    fields: ['eyebrow', 'cta_href_only', 'cta_secondary'],
    ownedElsewhere: {
      what: 'The heading, the body text, the first button label and the six capability cards',
      where: 'Provider Benefits',
      tab: 'provider-benefits',
    } },
  { key: 'founding', label: 'Founding provider programme', order: 10,
    blurb: 'The launch-cohort panel, its three lines and the terms note. Still shown only where campaigns are permitted.',
    fields: ['title', 'description', 'footnote'],
    footnoteLabel: 'Terms note',
    items: { label: 'Programme lines', withBody: false } },
  { key: 'academy', label: 'TreatX Academy', order: 11,
    blurb: 'Announcement copy, the session-status chip, the registration note and the Academy image.',
    fields: ['eyebrow', 'title', 'description', 'badge_label', 'footnote'],
    badgeLabel: 'Session status chip',
    footnoteLabel: 'Registration note',
    items: { label: 'Academy announcement card (optional image)', withBody: true, withImage: true } },
  { key: 'apps', label: 'Mobile apps', order: 12,
    blurb: 'The Coming Soon strip: its text and the badge wording.',
    fields: ['title', 'description', 'badge_label'],
    badgeLabel: 'Badge ("Coming soon")' },
  { key: 'final_cta', label: 'Final call to action', order: 13,
    blurb: 'Closing heading and both buttons.',
    fields: ['title', 'description', 'cta', 'cta_secondary'] },
];
